import { LockKeyhole } from 'lucide-react';
import { useContext, useState } from 'react';
import { deviceKey, type ApiClient, type SignInAnswer } from '@despachador/api-client';
import { Field, Notice } from '../index';
import { AuthCard, AuthLayoutContext, authMessage, errorCode, IdentifierField, PasswordToggle, TenantField, type AuthBrand } from './pieces';
import { MOTIVE_OF_CODE, type StatusMotive } from './StatusScreen';

export interface SignInProps {
  api: ApiClient;
  brand: AuthBrand;
  subtitle: string;
  /** Show the company field: the address does not identify the company (development). */
  askTenant: boolean;
  /** Company slug to send when the field is not shown (from `?tenant=`); never in production. */
  tenant: string | null;
  platform: 'WEB' | 'PWA';
  large?: boolean;
  /** What was typed before (kept when the person comes back from a later step). */
  identifier: string;
  onIdentifier: (v: string) => void;
  /** A message from the previous screen (e.g. the sign-in took too long). */
  notice?: string | null;
  /** Called on submit: the tap is a user gesture (the driver app unlocks alert audio with it). */
  onGesture?: () => void;
  onAnswer: (answer: SignInAnswer, tenant: string | null) => void;
  /** The credential was valid but the account cannot enter (suspended, disabled, company, app). */
  onRefused: (motive: StatusMotive) => void;
  /** «¿Olvidaste tu contraseña?»: the recovery (`/acceso/recuperar`). */
  onForgot: () => void;
  /**
   * Where «Continuar con Google» navigates (`GET /auth/oidc/google/start…`), only when this address
   * has sign-in with Google switched on (`branding.googleSignIn`). null = no button.
   */
  googleHref?: string | null;
}

/**
 * The only sign-in screen (docs/design-system.md): «Celular o correo» and password, or Google when
 * the company switched it on. Further steps (second factor, its mandatory setup) are screens of
 * their own, never errors. Wrong data always gets the same sentence, whether the account exists or not.
 */
export function SignIn(props: SignInProps) {
  const { api, brand, subtitle, askTenant, platform, large, identifier, onIdentifier } = props;
  const [tenantInput, setTenantInput] = useState(props.tenant ?? '');
  const [password, setPassword] = useState('');
  const [shown, setShown] = useState(false);
  const split = useContext(AuthLayoutContext) === 'split';
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    props.onGesture?.();
    setBusy(true);
    setError(null);
    const tenant = askTenant ? tenantInput.trim().toLowerCase() || null : props.tenant;
    try {
      const answer = await api.login({ identifier: identifier.trim(), password, tenant: tenant ?? undefined, deviceKey: deviceKey(), platform });
      setPassword('');
      props.onAnswer(answer, tenant);
    } catch (err) {
      const motive = MOTIVE_OF_CODE[errorCode(err) ?? ''];
      if (motive) {
        setPassword('');
        props.onRefused(motive);
      } else setError(authMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard
      brand={brand}
      title={subtitle}
      error={error}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {props.notice && <Notice tone="info">{props.notice}</Notice>}
      {askTenant && <TenantField value={tenantInput} onChange={setTenantInput} />}
      <IdentifierField value={identifier} onChange={onIdentifier} />
      <Field label="Contraseña" start={split ? <LockKeyhole size={16} /> : undefined} end={split ? <PasswordToggle shown={shown} onToggle={setShown} /> : undefined}>
        <input className="input" type={shown ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" maxLength={512} placeholder={split ? 'Tu contraseña' : undefined} required />
      </Field>
      {split ? (
        <div className="auth-links auth-links-end">
          <button type="button" className="btn ghost sm" onClick={props.onForgot}>
            ¿Olvidaste tu contraseña?
          </button>
        </div>
      ) : (
        <label className="check small">
          <input type="checkbox" checked={shown} onChange={(e) => setShown(e.target.checked)} /> Mostrar la contraseña
        </label>
      )}
      <button className={`btn primary block ${large ? 'lg' : ''}`} disabled={busy}>
        {busy ? 'Ingresando…' : 'Ingresar'}
      </button>
      {!split && (
        <button type="button" className="btn ghost sm" onClick={props.onForgot}>
          ¿Olvidaste tu contraseña?
        </button>
      )}
      {props.googleHref && (
        <>
          <div className="auth-divider" aria-hidden>
            <span>o</span>
          </div>
          {/* a full page navigation to the provider: never fetch */}
          <a className={`btn block ${large ? 'lg' : ''}`} href={props.googleHref} onClick={() => props.onGesture?.()}>
            <GoogleMark /> Continuar con Google
          </a>
        </>
      )}
      <p className="small muted" style={{ margin: 0 }}>
        Si no puedes ingresar, pide ayuda al administrador de tu empresa.
      </p>
    </AuthCard>
  );
}

/** Google's "G", as its brand guidelines ask for a sign-in button (decorative: the text names it). */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.5 13.6 17.8 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.4 5.8c4.3-4 6.9-9.9 6.9-17.2z" />
      <path fill="#FBBC05" d="M10.6 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.9-6.1z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.8c-2.1 1.4-4.8 2.3-8.5 2.3-6.2 0-11.5-4.1-13.4-9.8l-7.9 6.1C6.6 42.6 14.6 48 24 48z" />
    </svg>
  );
}
